// ShiplistFilter.tsx
import React from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';

import { ShipList } from '@ais-ui/api-spec/config';
import { useProfile } from '@ais-ui/profile';
import { FilterTrigger } from '../../../components/FilterTrigger';
import { ShiplistFilters } from './types';

interface ShiplistFilterProps {
  shiplistFilters: ShiplistFilters;
  setShiplistFilters: React.Dispatch<React.SetStateAction<ShiplistFilters>>;
  shiplists: ShipList[] | undefined;
}

// Extract unique owners from shiplists
const getOwnerOptions = (
  shiplists: ShipList[] | undefined,
  currentUsername: string | undefined,
) => {
  if (!shiplists || shiplists.length === 0) {
    return [];
  }

  const uniqueOwners = [
    ...new Set(shiplists.map((shiplist) => shiplist.owner).filter(Boolean)),
  ];

  // Separate current user from other owners
  const isCurrentUser = (owner: string | undefined) =>
    owner === currentUsername;
  const currentUserOwner = uniqueOwners.find(isCurrentUser);
  const otherOwners = uniqueOwners
    .filter((owner) => !isCurrentUser(owner))
    .sort((a, b) => (a || '').localeCompare(b || ''));

  // Build the final list with current user first (as "Me")
  const result: Array<{ value: string; label: string }> = [];

  if (currentUserOwner) {
    result.push({ value: currentUserOwner, label: 'Me' });
  }

  otherOwners.forEach((owner) => {
    if (owner) {
      result.push({ value: owner, label: owner });
    }
  });

  return result;
};

export const ShiplistFilter = ({
  shiplistFilters,
  setShiplistFilters,
  shiplists,
}: ShiplistFilterProps) => {
  const { username } = useProfile();
  const ownerOptions = getOwnerOptions(shiplists, username);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (shiplistFilters.owner !== 'all') count++;
    if (shiplistFilters.appliedAsFilter !== 'all') count++;
    if (shiplistFilters.colorBy !== 'all') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <FilterTrigger activeFilterCount={activeFiltersCount} />
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>
          {activeFiltersCount > 0
            ? `Filter shiplists (${activeFiltersCount} active)`
            : 'Filter shiplists'}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-64 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Filter Shiplists</div>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFiltersCount} active
            </Badge>
          )}
        </div>

        {/* Owner Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Owner</div>
          <Select
            value={shiplistFilters.owner}
            onValueChange={(value) =>
              setShiplistFilters((prev) => ({
                ...prev,
                owner: value,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All Owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {ownerOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Applied as Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Filter Status</div>
          <Select
            value={shiplistFilters.appliedAsFilter}
            onValueChange={(value) =>
              setShiplistFilters((prev) => ({
                ...prev,
                appliedAsFilter: value as ShiplistFilters['appliedAsFilter'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shiplists</SelectItem>
              <SelectItem value="applied">Applied as Filter</SelectItem>
              <SelectItem value="not-applied">Not Applied</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Color By Status */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Color Status</div>
          <Select
            value={shiplistFilters.colorBy}
            onValueChange={(value) =>
              setShiplistFilters((prev) => ({
                ...prev,
                colorBy: value as ShiplistFilters['colorBy'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shiplists</SelectItem>
              <SelectItem value="colored">Color Applied</SelectItem>
              <SelectItem value="not-colored">Not Colored</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() =>
              setShiplistFilters({
                owner: 'all',
                appliedAsFilter: 'all',
                colorBy: 'all',
              })
            }
            disabled={activeFiltersCount === 0}
          >
            Clear Filters
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
